import type { FixedIdAssetSnapshot } from '../holding-units/fixed-id-assets.dto.js';
import type { HoldingUnitEventEntityType } from '../holding-units/holding-unit-event-envelope.dto.js';
import type {
  HoldingUnitLoadSessionItemSelectionMethod,
  HoldingUnitLoadSessionMeasuredDiscrepancyAction,
  HoldingUnitLoadSessionStatus,
  HoldingUnitMeasuredDiscrepancyCommittedWeightEventType,
  HoldingUnitReservationState,
  HoldingUnitSnapshot,
} from '../holding-units/holding-units.dto.js';

export const HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_VERSION = 2 as const;

/**
 * SHA-256 of the stable schema identifier
 * `holding-units-canonical-stream-snapshot-schema:v2`.
 */
export const HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_HASH =
  'sha256:50609c21c2d48fa3faba106aed3bb1405e8dbf4ab06052de02529ac0c8024d4d' as const;

export interface HoldingUnitsCanonicalLatestWeightFact {
  eventId: string;
  streamVersion: number;
  eventType: 'holdingUnitWeightAdded' | 'holdingUnitWeightCorrected';
  occurredAt: string;
  actorUserId: string;
  grossLb: number | null;
  tareLb: number | null;
  netLb: number;
}

export interface HoldingUnitsCanonicalLatestShippedFact {
  eventId: string;
  streamVersion: number;
  occurredAt: string;
  shipmentId: string | null;
  coverShipmentId: string | null;
}

export interface HoldingUnitsCanonicalHoldingUnitState {
  kind: 'holdingUnit';
  snapshot: HoldingUnitSnapshot;
  reservation: HoldingUnitReservationState | null;
  latestWeight: HoldingUnitsCanonicalLatestWeightFact | null;
  latestShipped: HoldingUnitsCanonicalLatestShippedFact | null;
}

export interface HoldingUnitsCanonicalFixedIdAssetState {
  kind: 'fixedIdAsset';
  snapshot: FixedIdAssetSnapshot;
}

export interface HoldingUnitsCanonicalLoadSessionHead {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  status: HoldingUnitLoadSessionStatus;
  sessionName: string | null;
  toleranceProfileKey: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  lastEventId: string;
}

export interface HoldingUnitsCanonicalLoadSessionMaterialGroupState {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  loadSessionId: string;
  materialId: string;
  autoPickEnabled: boolean;
  toleranceProfileKey: string | null;
  yardLocationIds: string[] | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string | null;
  streamVersion: number;
}

export interface HoldingUnitsCanonicalLoadSessionItemState {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string;
  loadSessionId: string;
  groupId: string;
  unitId: string;
  selectionMethod: HoldingUnitLoadSessionItemSelectionMethod;
  snapshotNetLbAtSelection: number;
  snapshotGrossLbAtSelection: number | null;
  snapshotTareLbAtSelection: number | null;
  loadMeasuredGrossLb: number | null;
  loadMeasuredNetLb: number | null;
  deltaLossLb: number | null;
  toleranceProfileKey: string | null;
  capturedAt: string;
  capturedByUserId: string | null;
  wasAutoPicked: boolean;
  measuredDiscrepancyAction: HoldingUnitLoadSessionMeasuredDiscrepancyAction | null;
  measuredDiscrepancyResolvedAt: string | null;
  measuredDiscrepancyResolvedByUserId: string | null;
  measuredDiscrepancyCommittedWeightEventId: string | null;
  measuredDiscrepancyCommittedWeightEventType: HoldingUnitMeasuredDiscrepancyCommittedWeightEventType | null;
  removedAt: string | null;
  removedByUserId: string | null;
  streamVersion: number;
}

export interface HoldingUnitsCanonicalLoadSessionState {
  kind: 'loadSession';
  session: HoldingUnitsCanonicalLoadSessionHead;
  groups: HoldingUnitsCanonicalLoadSessionMaterialGroupState[];
  items: HoldingUnitsCanonicalLoadSessionItemState[];
}

export type HoldingUnitsCanonicalAggregateState =
  | HoldingUnitsCanonicalHoldingUnitState
  | HoldingUnitsCanonicalFixedIdAssetState
  | HoldingUnitsCanonicalLoadSessionState;

export interface HoldingUnitsCanonicalStreamSnapshot {
  entityType: HoldingUnitEventEntityType;
  entityId: string;
  baseOuterSequence: string;
  baseStreamVersion: number;
  lastEventId: string;
  createdAt: string;
  snapshotSchemaVersion: typeof HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_VERSION;
  snapshotSchemaHash: typeof HOLDING_UNITS_CANONICAL_STREAM_SNAPSHOT_SCHEMA_HASH;
  state: HoldingUnitsCanonicalAggregateState;
}

export interface HoldingUnitsCanonicalSnapshotRecord {
  kind: 'snapshot';
  headStreamVersion: number;
  snapshot: HoldingUnitsCanonicalStreamSnapshot;
}
